type Left<T> = {
    left: T;
    right?: never;
};

type Right<U> = {
    left?: never;
    right: U;
};

type Either<T, U> = NonNullable<Left<T> | Right<U>>;

type UnwrapEither = <T, U>(e: Either<T, U>) => NonNullable<T | U>;

const unwrapEither: UnwrapEither = <T, U>({left, right}: Either<T, U>) => {
    if (left !== undefined) {
        return left as NonNullable<T>; // Typescript is getting confused and returning this type as `T | undefined` unless we add the type assertion
    }

    return right as NonNullable<U>;
};

const isLeft = <T, U>(either: Either<T, U>): either is Left<T> => either.left !== undefined;

const isRight = <T, U>(either: Either<T, U>): either is Right<U> => either.right !== undefined;

const makeLeft = <T>(value: T): Left<T> => ({left: value});

const makeRight = <U>(value: U): Right<U> => ({right: value});

/**
 * Overloaded pipe function to support generic types for multiple pipe functions.
 * If the amount of types is not enough, more overloads can be added to support any use case.
 * When using the function, the types will be inferred correctly from the specific overload. The default `any` type will be used if no overload matches.
 */
interface Pipe {
    <T, U>(x: T, f: (x: T) => U): U;
    <T, U, V>(x: T, f: (x: T) => U, g: (x: U) => V): V;
    <T, U, V, W>(x: T, f: (x: T) => U, g: (x: U) => V, h: (x: V) => W): W;
    <T, U, V, W, X>(x: T, f: (x: T) => U, g: (x: U) => V, h: (x: V) => W, i: (x: W) => X): X;
    (arg: any, ...fns: ((arg: any) => any)[]): any;
}

const pipe: Pipe = (arg: any, ...fns: ((arg: any) => any)[]): any => fns.reduce((v, f) => f(v), arg);

export type {Either, Left, Right};
export {isLeft, isRight, makeLeft, makeRight, unwrapEither, pipe};
